
import { read, utils } from 'xlsx';
import { app } from './firebase';
import { getFirestore, collection, doc, setDoc, deleteDoc, getDocs, query, where, writeBatch, serverTimestamp } from 'firebase/firestore';
import { Asset, ImportMapping, AssetCategory, Supplier, CostCenter, User } from '../types';

const db = getFirestore(app);

export interface ImportPreviewRow {
    [key: string]: any;
    __rowNum__: number;
    __errors__?: string[];
}

export interface ImportError {
    row: number;
    column?: string;
    message: string;
    data: any;
}

export const ImportService = {
    // Helper to process promises in batches with a concurrency limit
    processInBatches: async <T, R>(
        items: T[],
        batchSize: number,
        fn: (batch: T[]) => Promise<R[]>,
        onProgress?: (processed: number, total: number) => void
    ): Promise<R[]> => {
        const results: R[] = [];
        const total = items.length;
        let processed = 0;

        for (let i = 0; i < total; i += batchSize) {
            const batch = items.slice(i, i + batchSize);
            const batchResults = await fn(batch);
            results.push(...batchResults);
            processed += batch.length;
            if (onProgress) onProgress(processed, total);
        }
        return results;
    },

    // Helper for date parsing
    parseDate: (val: any): string | null => {
        if (!val) return null;
        let str = val.toString().trim();
        if (!str || str === '-' || str.toLowerCase() === 'n/a') return null;

        // Try ISO first
        let d = new Date(str);
        if (!isNaN(d.getTime())) return d.toISOString();

        // Try DD/MM/YYYY
        // Regex for DD/MM/YYYY or D/M/YYYY
        let parts = str.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
        if (parts) {
            // Day is parts[1], Month parts[2], Year parts[3]
            // Note: Date constructor expects MM/DD/YYYY or YYYY-MM-DD
            // Construct YYYY-MM-DD
            const day = parts[1].padStart(2, '0');
            const month = parts[2].padStart(2, '0');
            const year = parts[3];
            d = new Date(`${year}-${month}-${day}`);
            if (!isNaN(d.getTime())) return d.toISOString();
        }

        // Try MM/DD/YYYY (if user locales differ, but standard in this context is likely EU)
        // If DD/MM fail logic above, maybe it was MM/DD?
        // Let's assume EU format priority for now based on '29/4/2023' example.

        return null;
    },

    // Helper for number parsing (Eu vs US)
    parseNumber: (val: any): number => {
        if (typeof val === 'number') return val;
        if (!val) return 0;
        let str = val.toString().trim();
        // Remove currency symbols/spaces/unknown chars but keep digits, commas, dots, minus
        str = str.replace(/[^0-9.,-]/g, '');
        if (!str) return 0;

        // Heuristic:
        // 1. If it has a comma, assume it's a decimal separator (European) -> "1,00" or "1.000,00"
        // 2. If it has dots and NO comma, check if it looks like thousands like "1.000" (EU) or "1.5" (US)

        if (str.includes(',')) {
            // European format confirmed (or mixed)
            // Remove thousands (dots)
            str = str.replace(/\./g, '');
            // Replace decimal comma with dot
            str = str.replace(/,/g, '.');
        } else if (str.includes('.')) {
            // Only dots. Ambiguous: "1.000" vs "1.5"
            // If it matches pattern of thousands (1-3 digits, dot, 3 digits...)
            if (/^\d{1,3}(\.\d{3})+$/.test(str)) {
                // Likely thousands separator
                str = str.replace(/\./g, '');
            }
            // Else assume US float "1.5" -> leave as is
        }

        return parseFloat(str) || 0;
    },

    // 1. Parsing
    parseFile: async (file: File): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = e.target?.result as ArrayBuffer;
                    // Force UTF-8 decoding which helps with Greek characters in CSVs
                    const decoder = new TextDecoder("utf-8");
                    const csvText = decoder.decode(data);

                    const workbook = read(csvText, { type: 'string' });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const jsonData = utils.sheet_to_json(sheet, { defval: "", raw: false });
                    resolve(jsonData);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = (err) => reject(err);
            reader.readAsArrayBuffer(file);
        });
    },

    // 2. Mapping Management
    getMappings: async (agencyId: string): Promise<ImportMapping[]> => {
        const q = query(collection(db, 'import_mappings'), where('agencyId', '==', agencyId));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() } as ImportMapping));
    },

    saveMapping: async (mapping: Omit<ImportMapping, 'id' | 'createdAt' | 'updatedAt'>) => {
        const newRef = doc(collection(db, 'import_mappings'));
        await setDoc(newRef, {
            ...mapping,
            id: newRef.id,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
        return newRef.id;
    },

    deleteMapping: async (id: string) => {
        await deleteDoc(doc(db, 'import_mappings', id));
    },

    // 3. Validation & Resolution
    validateAndResolve: async (
        data: any[],
        mapping: Record<string, string | string[]>,
        agencyId: string,
        existingCategories: AssetCategory[],
        existingSuppliers: Supplier[],
        existingCostCenters: CostCenter[],
        existingBranches?: User[], // Added optional for backward compatibility if needed, but we passed it
        existingTeam?: User[], // Added for Manager/User Email resolution
        onProgress?: (status: string, progress: number) => void // Added Progress Callback
    ) => {
        const errors: ImportError[] = [];
        const preview: ImportPreviewRow[] = [];
        const newCategories = new Map<string, Partial<AssetCategory> & { _parentTitleRef?: string }>();
        const newSuppliers = new Map<string, Partial<Supplier>>();
        const newCostCenters = new Map<string, Partial<CostCenter>>();

        // Helper to normalize strings
        const norm = (s: string) => s?.toString().trim().toLowerCase() || '';

        const seenSerials = new Set<string>();
        const seenBarcodes = new Set<string>();
        // Removed seenCodes since code is no longer required to be unique

        // Map Branch Code -> User (Branch)
        // We look for profile.branchCode
        // Also Map Email -> User ID for Team and Branches
        const branchMap = new Map<string, User>();
        const emailMap = new Map<string, string>(); // Email -> UserID

        const branchNameMap = new Map<string, string>(); // Name/Company -> UserID

        if (existingBranches) {
            existingBranches.forEach(b => {
                const code = b.profile?.branchCode;
                if (code) {
                    branchMap.set(code.toString().trim().toLowerCase(), b);
                }
                if (b.email) {
                    emailMap.set(b.email.trim().toLowerCase(), b.id);
                }
                if (b.profile?.companyName) {
                    const n = norm(b.profile.companyName);
                    branchNameMap.set(n, b.id);
                    console.log(`[Import Setup] Branch Map: '${n}' -> ${b.id}`);
                }
                if (b.displayName) {
                    const n = norm(b.displayName);
                    branchNameMap.set(n, b.id);
                    console.log(`[Import Setup] Branch Map: '${n}' -> ${b.id}`);
                }
            });
            console.log(`[Import Setup] Total Branch Names Mapped: ${branchNameMap.size}`);
        }

        if (existingTeam) {
            existingTeam.forEach(t => {
                if (t.email) {
                    emailMap.set(t.email.trim().toLowerCase(), t.id);
                }
            });
        }


        // 1. Fetch Existing Barcodes from DB to check for potential updates
        const allBarcodes = data
            .map(row => {
                const barcodeHeader = Object.keys(mapping).find(key => {
                    const val = mapping[key];
                    return Array.isArray(val) ? val.includes('barcode') : val === 'barcode';
                });
                return barcodeHeader ? row[barcodeHeader]?.toString().trim() : null;
            })
            .filter(c => c);

        const existingDbBarcodes = new Map<string, string>(); // Barcode -> AssetID
        if (allBarcodes.length > 0) {
            const uniqueBarcodes = Array.from(new Set(allBarcodes));
            if (onProgress) onProgress("Validating Barcodes...", 10);

            await ImportService.processInBatches(uniqueBarcodes, 20, async (chunk) => {
                const q = query(
                    collection(db, 'assets'),
                    where('agencyId', '==', agencyId),
                    where('barcode', 'in', chunk)
                );
                const snaps = await getDocs(q);
                const results: any[] = [];
                snaps.forEach(d => {
                    const b = norm(d.data().barcode);
                    if (b) existingDbBarcodes.set(b, d.id);
                    results.push(d.id);
                });
                return results;
            }, (p, t) => {
                // scale 10-40%
                if (onProgress) onProgress("Validating Barcodes...", 10 + Math.round((p / t) * 30));
            });
        }

        // Removed Existing Codes DB check. We rely on Barcode for uniqueness/updates now.

        // Pre-index existing entities
        if (onProgress) onProgress("Finalizing Validation...", 80);
        const catMap = new Map(existingCategories.map(c => [norm(c.title), c]));
        const supMap = new Map(existingSuppliers.map(s => [norm(s.name), s]));
        const ccMap = new Map(existingCostCenters.map(c => [norm(c.code), c])); // Verify unique by Code

        data.forEach((row, index) => {
            const rowNum = index + 1;
            const mappedRow: any = { __rowNum__: rowNum };
            const rowErrors: string[] = [];

            // Apply Mapping
            Object.entries(mapping).forEach(([csvHeader, assetFieldOrFields]) => {
                let value = row[csvHeader];
                if (typeof value === 'string') value = value.trim();

                const fields = Array.isArray(assetFieldOrFields) ? assetFieldOrFields : [assetFieldOrFields];
                fields.forEach(field => {
                    mappedRow[field] = value;
                });
            });

            // Resolve Emails to IDs for Manager/User
            if (mappedRow.assetManagerId) {
                const normEmail = norm(mappedRow.assetManagerId);
                if (emailMap.has(normEmail)) {
                    const resolvedId = emailMap.get(normEmail);
                    console.log(`[Import] Resolved Manager Email '${mappedRow.assetManagerId}' -> ID '${resolvedId}'`);
                    mappedRow.assetManagerId = resolvedId;
                } else {
                    console.log(`[Import] Failed to resolve Manager Email '${mappedRow.assetManagerId}' (Not found in ${emailMap.size} emails)`);
                }
            }
            if (mappedRow.assetUserId) {
                const normEmail = norm(mappedRow.assetUserId);
                if (emailMap.has(normEmail)) {
                    mappedRow.assetUserId = emailMap.get(normEmail);
                }
            }


            // Resolve Branch Name to ID
            if (mappedRow.branchId) {
                const normBranch = norm(mappedRow.branchId);
                let resolved = false;

                if (branchNameMap.has(normBranch)) {
                    const resolvedId = branchNameMap.get(normBranch);
                    console.log(`[Import] Resolved Branch Name '${mappedRow.branchId}' -> ID '${resolvedId}'`);
                    mappedRow.branchId = resolvedId;
                    resolved = true;
                } else if (branchMap.has(normBranch)) {
                    // Try code map as fallback
                    mappedRow.branchId = branchMap.get(normBranch)!.id;
                    resolved = true;
                }

                if (!resolved) {
                    console.log(`[Import] Failed to resolve Branch '${mappedRow.branchId}'. Clearing field.`);
                    mappedRow.branchId = ''; // Prevent saving the raw name as ID!
                }
            }

            if (!mappedRow.code) {
                rowErrors.push('Missing Code');
            }

            // Soft Defaults for others
            if (!mappedRow.description) {
                if (mappedRow.code) mappedRow.description = `Asset ${mappedRow.code}`;
                else rowErrors.push('Missing Description');
            }

            if (!mappedRow.categoryId) {
                mappedRow.categoryId = 'General'; // Default category
            }

            // Duplicate Serial Check (In-File)
            if (mappedRow.serialNumber) {
                const sn = norm(mappedRow.serialNumber);
                const invalid = ['-', 'n/a', 'na', 'none', 'null', ''];
                if (!invalid.includes(sn)) {
                    if (seenSerials.has(sn)) {
                        rowErrors.push(`Duplicate Serial Number in file: ${mappedRow.serialNumber}`);
                    } else {
                        seenSerials.add(sn);
                    }
                }
            }

            // Duplicate Barcode Check (In-File)
            if (mappedRow.barcode) {
                const bc = norm(mappedRow.barcode);
                const invalid = ['-', 'n/a', 'na', 'none', 'null', ''];
                if (!invalid.includes(bc)) {
                    if (seenBarcodes.has(bc)) {
                        rowErrors.push(`Duplicate Barcode in file: ${mappedRow.barcode}`);
                    } else {
                        seenBarcodes.add(bc);
                    }

                    // Check for Existing in DB (Update Candidate)
                    if (existingDbBarcodes.has(bc)) {
                        // Mark as validation "update" candidate, NOT error
                        // Mark as validation "update" candidate, NOT error
                        mappedRow.__existingId__ = existingDbBarcodes.get(bc);
                    }
                }
            }

            // Fallback: Code is no longer used for matching as it can be non-unique.

            // Note: Entity Resolution will now create "General" category if needed

            // Entity Resolution

            // Category
            // Category & Subcategory Logic
            if (mappedRow.categoryId) { // mappedRow.categoryId currently holds the CATEGORY NAME (or Parent if subcategory exists)
                const catName = mappedRow.categoryId;
                const subCatName = mappedRow.subcategory;

                if (subCatName) {
                    // We have a hierarchy: Parent = catName, Child = subCatName
                    // 1. Ensure Parent exists or is registered
                    const pToken = norm(catName);
                    if (!catMap.has(pToken) && !newCategories.has(pToken)) {
                        newCategories.set(pToken, {
                            title: catName,
                            code: pToken.replace(/\s+/g, '-').toUpperCase(),
                            agencyId
                        });
                    }

                    // 2. Ensure Child exists or is registered
                    // Note: This logic assumes unique titles globally for simplicity.
                    const cToken = norm(subCatName);
                    if (!catMap.has(cToken) && !newCategories.has(cToken)) {
                        newCategories.set(cToken, {
                            title: subCatName,
                            code: cToken.replace(/\s+/g, '-').toUpperCase(),
                            agencyId,
                            _parentTitleRef: catName // Internal marker to resolve ID later
                        });
                    }

                    // 3. Point the asset to the CHILD category
                    mappedRow.categoryId = subCatName;
                    // We overwrite it so that 'executeImport' logic (which looks up mappedRow.categoryId) checks for the Child.
                } else {
                    // Standard single-level category
                    const nToken = norm(catName);
                    if (!catMap.has(nToken) && !newCategories.has(nToken)) {
                        newCategories.set(nToken, {
                            title: catName,
                            code: nToken.replace(/\s+/g, '-').toUpperCase(), // Use 'code' instead of 'slug', assuming code exists
                            agencyId
                        });
                    }
                }
            }

            // Supplier
            const supName = mappedRow.supplierId || mappedRow.supplierDescription;
            if (supName) {
                const nToken = norm(supName);
                if (!supMap.has(nToken) && !newSuppliers.has(nToken)) {
                    newSuppliers.set(nToken, {
                        name: supName,
                        code: mappedRow.supplierCode || '',
                        vatNumber: mappedRow.supplierVat || '',
                        agencyId
                    });
                }
                // Ensure supplierId is set for execution mapping
                if (!mappedRow.supplierId) {
                    mappedRow.supplierId = supName;
                }
            }

            // Cost Center (assuming mapped to 'costCenterId' which holds Code? or Name?)
            // Let's assume input is CODE.
            if (mappedRow.costCenterId) {
                const ccCode = mappedRow.costCenterId;
                const nToken = norm(ccCode);
                if (!ccMap.has(nToken) && !newCostCenters.has(nToken)) {
                    newCostCenters.set(nToken, {
                        code: ccCode,
                        title: `Cost Center ${ccCode}`, // Fallback title
                        agencyId
                    });
                }
            }

            // Branch Resolution
            if (mappedRow.branchCode) {
                const bCode = norm(mappedRow.branchCode);
                const branch = branchMap.get(bCode);
                if (branch) {
                    mappedRow.branchId = branch.id;
                }
            }

            if (rowErrors.length > 0) {
                mappedRow.__errors__ = rowErrors;
                rowErrors.forEach(msg => errors.push({ row: rowNum, message: msg, data: row }));
            }
            preview.push(mappedRow);
        });

        return {
            preview,
            errors,
            newEntities: {
                categories: Array.from(newCategories.values()),
                suppliers: Array.from(newSuppliers.values()),
                costCenters: Array.from(newCostCenters.values())
            },
            updates: preview.filter(r => r.__existingId__)
        };
    },

    // 4. Execution
    executeImport: async (
        validData: any[],
        newEntities: { categories: any[], suppliers: any[], costCenters: any[] },
        agencyId: string,
        resolvedMaps?: {
            catMap: Map<string, string>, // Name -> ID
            supMap: Map<string, string>,
            ccMap: Map<string, string>
        },
        onProgress?: (status: string, progress: number) => void
    ) => {
        if (onProgress) onProgress("Creating New Entities...", 10);
        const batch = writeBatch(db);

        // 1. Create New Entities
        const catIdMap = new Map<string, string>(resolvedMaps?.catMap);
        const supIdMap = new Map<string, string>(resolvedMaps?.supMap);
        const ccIdMap = new Map<string, string>(resolvedMaps?.ccMap);

        const norm = (s: string) => s?.toString().trim().toLowerCase() || '';

        // Sort new categories: Parents first (those without _parentTitleRef)
        // This ensures that when we process a child, the parent might have been created and added to catIdMap
        newEntities.categories.sort((a, b) => {
            if (a._parentTitleRef && !b._parentTitleRef) return 1;
            if (!a._parentTitleRef && b._parentTitleRef) return -1;
            return 0;
        });

        newEntities.categories.forEach(c => {
            const ref = doc(collection(db, 'asset_categories'));

            // Resolve Parent ID if this is a subcategory
            let parentId = null;
            if (c._parentTitleRef) {
                // Try to find parent in existing map or just-added map (same map)
                parentId = catIdMap.get(norm(c._parentTitleRef));
            }

            const { _parentTitleRef, ...categoryData } = c; // Remove internal ref
            const finalData = {
                ...categoryData,
                id: ref.id,
                type: 'category',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            if (parentId) {
                (finalData as any).parentId = parentId;
            }

            batch.set(ref, finalData);
            catIdMap.set(norm(c.title), ref.id);
        });

        newEntities.suppliers.forEach(s => {
            const ref = doc(collection(db, 'suppliers'));
            batch.set(ref, { ...s, id: ref.id, type: 'supplier', createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
            supIdMap.set(norm(s.name), ref.id);
        });

        newEntities.costCenters.forEach(cc => {
            const ref = doc(collection(db, 'costCenters'));
            batch.set(ref, { ...cc, id: ref.id, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
            ccIdMap.set(norm(cc.code), ref.id);
        });

        // 2. Commit Entities First
        await batch.commit();

        return {
            createdCategories: newEntities.categories.length,
            createdSuppliers: newEntities.suppliers.length,
            createdCostCenters: newEntities.costCenters.length,
            maps: {
                catIdMap,
                supIdMap,
                ccIdMap
            }
        };
    },

    // Final Batch Import of Assets
    batchImportAssets: async (
        assets: any[],
        agencyId: string,
        maps: { catIdMap: Map<string, string>, supIdMap: Map<string, string>, ccIdMap: Map<string, string> },
        updateMode: boolean = false,
        splitByQuantity: boolean = false,
        onProgress?: (progress: number, total: number) => void
    ) => {
        const norm = (s: string) => s?.toString().trim().toLowerCase() || '';

        // 1. Pre-allocate IDs and build Internal Map
        // We map BOTH Code and SerialNumber to the new Ref
        const assetPreMap = new Map<string, { ref: any, row: any }>();
        const externalMasters = new Set<string>();

        assets.forEach(row => {
            // Determine Ref: New or Existing (if updateMode is ON and row has __existingId__)
            let ref;
            if (updateMode && row.__existingId__) {
                ref = doc(db, 'assets', row.__existingId__);
            } else {
                ref = doc(collection(db, 'assets'));
            }

            // Store by Serial (if valid)
            if (row.serialNumber) {
                const sn = norm(row.serialNumber);
                const invalid = ['-', 'n/a', 'na', 'none', 'null', ''];
                if (!invalid.includes(sn)) {
                    assetPreMap.set(sn, { ref, row });
                }
            }
            // Store by Code (Always valid usually)
            if (row.code) {
                assetPreMap.set(norm(row.code), { ref, row });
            }

            // We attach the ref to the row temporarily for easy access later
            row.__tempRef__ = ref;
        });

        // Check for external dependencies
        assets.forEach(row => {
            if (row.masterSerial) {
                const ms = norm(row.masterSerial);
                // If not found internally (by code or serial), it's external
                if (!assetPreMap.has(ms)) {
                    externalMasters.add(row.masterSerial);
                }
            }
        });

        // 2. Resolve External Masters (Chunked Query)
        // We need to look up by SerialNumber AND Code
        const externalGroupIdMap = new Map<string, string>();
        const extList = Array.from(externalMasters);

        if (extList.length > 0) {
            await ImportService.processInBatches(extList, 20, async (chunk) => {
                // Query A: By serialNumber
                const q1 = query(
                    collection(db, 'assets'),
                    where('agencyId', '==', agencyId),
                    where('serialNumber', 'in', chunk)
                );
                // Query B: By code
                const q2 = query(
                    collection(db, 'assets'),
                    where('agencyId', '==', agencyId),
                    where('code', 'in', chunk)
                );

                const [snaps1, snaps2] = await Promise.all([getDocs(q1), getDocs(q2)]);
                const results: any[] = [];

                const processDoc = (d: any) => {
                    const dData = d.data();
                    const gId = dData.groupId || d.id;
                    if (dData.serialNumber) {
                        externalGroupIdMap.set(dData.serialNumber, gId);
                        externalGroupIdMap.set(norm(dData.serialNumber), gId);
                    }
                    if (dData.code) {
                        externalGroupIdMap.set(dData.code, gId);
                        externalGroupIdMap.set(norm(dData.code), gId);
                    }
                    results.push(d.id);
                };

                snaps1.forEach(processDoc);
                snaps2.forEach(processDoc);
                return results;
            });
        }

        // 3. Pre-fetch Existing Child Assets (for Split Logic Updates)
        const generatedChildCodes: string[] = [];
        assets.forEach(row => {
            const qty = Math.round(ImportService.parseNumber(row.quantity)) || 1;
            if (qty > 1 && row.code) {
                for (let i = 0; i < qty; i++) {
                    generatedChildCodes.push(`${row.code}-${i + 1}`);
                }
            }
        });

        const existingChildMap = new Map<string, string>(); // Code -> ID
        if (generatedChildCodes.length > 0) {
            const uniqueCodes = Array.from(new Set(generatedChildCodes));
            await ImportService.processInBatches(uniqueCodes, 20, async (chunk) => {
                const q = query(
                    collection(db, 'assets'),
                    where('agencyId', '==', agencyId),
                    where('code', 'in', chunk)
                );
                const snaps = await getDocs(q);
                const results: any[] = [];
                snaps.forEach(d => {
                    const c = norm(d.data().code);
                    if (c) existingChildMap.set(c, d.id);
                    results.push(d.id);
                });
                return results;
            });
        }

        // 4. Assign Group IDs
        const preparedAssets: any[] = [];

        for (const row of assets) {
            // SKIP condition: If updateMode is FALSE, and this row is existing, we skip it.
            if (!updateMode && row.__existingId__) {
                continue; // Skip this asset completely
            }

            const ref = row.__tempRef__; // Use pre-allocated ref
            let groupId = ref.id; // Default: Self-Master

            if (row.masterSerial) {
                const ms = norm(row.masterSerial);

                // A. Check Internal (Code or Serial)
                if (assetPreMap.has(ms)) {
                    const masterNode = assetPreMap.get(ms);
                    if (masterNode) groupId = masterNode.ref.id;
                }
                // B. Check External
                else if (externalGroupIdMap.has(ms)) {
                    groupId = externalGroupIdMap.get(ms)!;
                }
                else if (externalGroupIdMap.has(row.masterSerial)) {
                    groupId = externalGroupIdMap.get(row.masterSerial)!;
                }
            }

            const catId = maps.catIdMap.get(norm(row.categoryId)) || row.categoryId || '';
            const supplierNameKey = row.supplierId || row.supplierDescription || '';
            const supId = maps.supIdMap.get(norm(supplierNameKey)) || supplierNameKey;
            const ccId = maps.ccIdMap.get(norm(row.costCenterId)) || row.costCenterId || '';

            // Helper to parse status locally
            const parseStatus = (val: string): string => {
                if (!val) return 'active';
                const s = val.toString().trim().toLowerCase();
                // Map Greek/Localized to English Enum
                if (s === 'ενεργό' || s === 'active') return 'active';
                if (s === 'προσωρινά ανενεργό' || s === 'temporarily_inactive') return 'temporarily_inactive';
                if (s === 'ανενεργό' || s === 'inactive') return 'inactive';
                if (s === 'πλήρως αποσβεσμένο' || s === 'fully_depreciated') return 'fully_depreciated';
                if (s === 'υπό πώληση' || s === 'for_sale') return 'for_sale';
                if (s === 'πωληθέν' || s === 'sold') return 'sold';
                if (s === 'διαγραμμένο' || s === 'deleted') return 'deleted';
                if (s === 'διαγραφή (λογιστική)' || s === 'written_off') return 'written_off';
                if (s === 'ακυρωμένο' || s === 'cancelled') return 'cancelled';
                if (s === 'προς καταστροφή' || s === 'to_be_destroyed') return 'to_be_destroyed';
                if (s === 'προς μετακίνηση' || s === 'to_be_moved') return 'to_be_moved';
                // Fallback: try default replace
                return s.replace(/\s+/g, '_');
            };

            // Prepare Base Properties
            const baseProperties = {
                ...row,
                agencyId,
                status: parseStatus(row.status),
                categoryId: catId,
                supplierId: supId,
                supplierDescription: row.supplierDescription || '',
                supplierCode: row.supplierCode || '',
                supplierVat: row.supplierVat || '',
                branchCode: row.branchCode || '',
                branchId: row.branchId || '',
                costCenterId: ccId,
                color: row.color || '',
                height: row.height || '',
                length: row.length || '',
                width: row.width || '',
                quantity: 1, // Will be forced to 1 for individual assets
                unitValue: ImportService.parseNumber(row.unitValue) || 0,
                // Default totalValue to row.totalValue (from CSV GTotal)
                // We will override this for split Children later
                totalValue: ImportService.parseNumber(row.totalValue) || ImportService.parseNumber(row.unitValue) || 0,
                residualValue: ImportService.parseNumber(row.residualValue) || 0,
                vatPercent: ImportService.parseNumber(row.vatPercent) || 24,
                discountStats: ImportService.parseNumber(row.discountStats) || ImportService.parseNumber(row.discount) || 0,
                discount: ImportService.parseNumber(row.discount) || 0,
                acquisitionDate: ImportService.parseDate(row.acquisitionDate) || new Date().toISOString(),
                invoiceDate: ImportService.parseDate(row.invoiceDate) || '',
                sentToAccountingDate: ImportService.parseDate(row.sentToAccountingDate) || '',
                usageStartDate: ImportService.parseDate(row.usageStartDate) || '',
                model: row.model || '',
                manufacturer: row.manufacturer || '',
                serialNumber: row.serialNumber || '',
                masterSerial: row.masterSerial || '',
                barcode: row.barcode || '',
                invoiceNumber: row.invoiceNumber || '',
                exactLocation: row.exactLocation || '',
                notes: row.notes || '',
                assetManagerId: row.assetManagerId || '',
                assetUserId: row.assetUserId || '',
                groupId: groupId,
                updatedAt: new Date().toISOString(),
            };

            const quantity = Math.round(ImportService.parseNumber(row.quantity)) || 1;
            console.log(`[Import Debug] Processing Row Code: ${row.code}, Quantity: ${quantity}, IsUpdate: ${updateMode}, ExistingID: ${row.__existingId__}`);

            if (quantity > 1 && splitByQuantity) {
                // Strategy: 1 Parent + N Children (Requested by User)

                // 1. Create Parent Reference (The CSV Entry)
                // This acts as the "Batch Header" or Master Record for this group
                const parentAsset: any = {
                    ...baseProperties,
                    id: ref.id,
                    quantity: quantity, // Parent keeps total batch quantity
                };

                if (!row.__existingId__) {
                    parentAsset.createdAt = new Date().toISOString();
                }

                // Cleanup internal keys for Parent as well
                delete parentAsset.__rowNum__;
                delete parentAsset.__errors__;
                delete parentAsset.__tempRef__;
                delete parentAsset.__existingId__;

                if (!!row.__existingId__ && updateMode) {
                    // Should Not Update these fields if existing
                    if (!row.status) delete parentAsset.status;
                    if (!parentAsset.branchId) delete parentAsset.branchId;
                }

                // If not updateMode, we are creating NEW, so isUpdate is false
                preparedAssets.push({ ref: ref, data: parentAsset, isUpdate: !!row.__existingId__ && updateMode });
                console.log(`[Import Debug] Pushed Parent: ${parentAsset.code} (ID: ${ref.id})`);

                // 2. Create Children
                for (let i = 0; i < quantity; i++) {
                    const childCode = `${row.code}-${i + 1}`;

                    // Check if Child Already Exists (by Code)
                    // Note: We use normalizing 'norm' logic if needed, but keys are usually strict
                    const existingChildId = existingChildMap.get(norm(childCode));

                    const childRef = existingChildId ? doc(db, 'assets', existingChildId) : doc(collection(db, 'assets'));
                    const isChildUpdate = !!existingChildId;

                    // Children are essentially copies but with individual tracking
                    const childAsset: any = {
                        ...baseProperties,
                        id: childRef.id,
                        code: childCode, // Code-1, Code-2... to Code-N
                        masterSerial: row.code, // Link to Parent Code
                        serialNumber: '', // Clear unique identifiers
                        barcode: '',
                        quantity: 1, // Child is 1 unit
                        totalValue: baseProperties.unitValue, // Value = Unit Value
                        groupId: ref.id, // Grouped under Parent
                    };

                    // Only set createdAt if new
                    if (!isChildUpdate) {
                        childAsset.createdAt = new Date().toISOString();
                    }

                    // Cleanup internal keys
                    delete childAsset.__rowNum__;
                    delete childAsset.__errors__;
                    delete childAsset.__tempRef__;
                    delete childAsset.__existingId__;

                    if (isChildUpdate) {
                        // Protect fields on update
                        if (!row.status) delete childAsset.status;
                        if (!childAsset.branchId) delete childAsset.branchId;
                    }

                    preparedAssets.push({ ref: childRef, data: childAsset, isUpdate: isChildUpdate });
                }

            } else {
                // Quantity = 1 (Standard Single Asset)
                const finalAsset: any = {
                    ...baseProperties,
                    id: ref.id,
                    quantity: quantity // Use actual quantity (it will be 1 if splitByQuantity is true and we are in this block? No, if splitByQuantity is true and quantity > 1 we are in the IF block. If quantity <= 1 we are here. If splitByQuantity is false we are here regardless of quantity.)
                };

                if (!row.__existingId__) {
                    finalAsset.createdAt = new Date().toISOString();
                }

                delete finalAsset.__rowNum__;
                delete finalAsset.__errors__;
                delete finalAsset.__tempRef__;
                delete finalAsset.__existingId__;

                const isUpdate = !!row.__existingId__;

                if (isUpdate) {
                    if (!row.status) delete finalAsset.status;
                    if (!finalAsset.branchId) delete finalAsset.branchId;
                }

                preparedAssets.push({ ref, data: finalAsset, isUpdate });
            }
        }

        // 4. Batch Write
        const chunkSize = 400;
        const results = { success: 0, failed: 0, errors: [] as any[] };

        for (let i = 0; i < preparedAssets.length; i += chunkSize) {
            const chunk = preparedAssets.slice(i, i + chunkSize);
            const batch = writeBatch(db);

            chunk.forEach(item => {
                if (item.isUpdate) {
                    batch.update(item.ref, item.data);
                } else {
                    batch.set(item.ref, item.data);
                }
            });

            try {
                await batch.commit();
                results.success += chunk.length;
                if (onProgress) onProgress(Math.min(i + chunkSize, preparedAssets.length), preparedAssets.length);
            } catch (e: any) {
                console.error("Batch failed", e);
                results.failed += chunk.length;
                results.errors.push(e.message);
            }
        }
        return results;
    }
};
